import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@/lib/api/types'
import { toast } from '@/components/ui/Toast'

export interface ConsultationCreateRequest {
  facilityId: number
  guardianName: string
  guardianPhone: string
  careGrade?: string
  message?: string
}

export interface ConsultationResponse {
  id: number
  facilityId: number
  facilityName: string
  userId: number | null
  guardianName: string
  guardianPhone: string
  careGrade: string | null
  message: string | null
  status: 'PENDING' | 'FORWARDED' | 'RESPONDED' | 'CLOSED'
  adminMemo: string | null
  facilityReply: string | null
  forwardedAt: string | null
  respondedAt: string | null
  createdAt: string
}

interface PageResponse {
  content: ConsultationResponse[]
  totalElements: number
  totalPages: number
  number: number
  size: number
  last: boolean
}

/** 입소 상담 요청 등록 */
export function useSubmitConsultation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ConsultationCreateRequest) => {
      const res = await apiClient.post<ApiResponse<ConsultationResponse>>(
        '/consultations',
        data
      )
      return res.data.data
    },
    onSuccess: () => {
      toast.success('입소 상담 요청이 접수되었습니다')
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
    },
    onError: () => {
      toast.error('상담 요청에 실패했습니다')
    },
  })
}

/** 내 상담 요청 목록 */
export function useMyConsultations(page = 0) {
  return useQuery({
    queryKey: ['consultations', 'my', page],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<PageResponse>>(
        `/consultations/my?page=${page}&size=20`
      )
      return res.data.data
    },
    retry: false,
    placeholderData: { content: [], totalElements: 0, totalPages: 0, number: 0, size: 20, last: true },
  })
}
